import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = Array.from({ length: 4 }, (_, i) => comic);

  return (
    <>
      <SEOSchema type="Article" data={{
        headline: `${comic.title} - 韩漫在线免费看`,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.updatedAt,
        image: comic.coverImage,
        url: `https://www.koreancomic.cn/comic/${comic.id}`,
      }} />
      <SEOSchema type="WebPage" data={{
        name: `${comic.title} - 韩国漫画网`,
        description: `${comic.title}，${comic.category}类韩漫免费漫画，作者${comic.author}。${comic.description.substring(0, 100)}`,
        url: `https://www.koreancomic.cn/comic/${comic.id}`,
      }} />
      <Navbar />
      <main>
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: comic.category, href: `/category/${comic.categorySlug}` },
          { label: comic.title },
        ]} />

        <article className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-[280px] aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩漫免费漫画在线阅读`}
                    title={`${comic.title} - 韩漫在线免费看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'}`}>
                    {comic.status}
                  </span>
                  <span className="px-2 py-1 text-xs rounded-full bg-accent-100 text-accent-700">{comic.category}</span>
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                  <span>作者：<strong className="text-foreground-700">{comic.author}</strong></span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill"></i>
                    </div>
                    <strong className="text-foreground-700">{comic.rating}</strong>
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </div>
                    <strong className="text-foreground-700">{comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}</strong>
                  </span>
                  <span>更新于：<strong className="text-foreground-700">{comic.updatedAt}</strong></span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600 border border-background-200/70">{tag}</span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">内容简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/comic/${comic.id}/read`}
                    className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                    <span className="ml-2">
                      <i className="ri-play-line"></i>
                    </span>
                  </Link>
                  <button className="px-6 py-3 rounded-lg border border-background-200/70 text-foreground-600 font-medium text-sm hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center inline mr-1">
                      <i className="ri-bookmark-line"></i>
                    </div>
                    收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="py-8 md:py-12 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">章节列表</h2>
            <div className="space-y-2 max-w-3xl">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/comic/${comic.id}/read`}
                  className="flex items-center justify-between p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-file-text-line"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">{chapter.title}</p>
                      <p className="text-xs text-foreground-400 mt-0.5">{chapter.updatedAt} · {chapter.views > 10000 ? `${(chapter.views / 10000).toFixed(1)}万阅读` : `${chapter.views}阅读`}</p>
                    </div>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center text-foreground-400 group-hover:text-primary-500 transition-colors flex-shrink-0 ml-3">
                    <i className="ri-arrow-right-s-line"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {relatedComics.map((rc, i) => (
                <ComicCard key={`related-${i}`} comic={rc} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}